import { useCallback, useState, useRef, useEffect } from "react";
import {
  Copy,
  Check,
  Icon,
  CircleAlert,
  CircleCheck,
  Undo2,
  Loader2,
} from "lucide-react";
import { Button } from "@/toggle-ui-kit/button";
import { toast } from "sonner";
import {
  EditableRowItemV2,
  EditableTableItemsV2,
  EditableTablePreviewResponseV2,
  type UpdateCellValueRequest,
} from "@/apis/editable-preview/types";
import { usePreviewStore } from "@/stores/preview";
import {
  ArrowedButtonTooltip,
  TooltipWrapper,
} from "@/components/common/tooltip";
import AutoSumToggle from "../auto-sum-toggle";
import {
  useUpdateAutoSumState,
  useUpdateTransposeState,
} from "../sidebar/hook";
import { useUpdateCellValue } from "./use-editable-preview";
import { cn } from "@/lib/utils";
import {
  TableUnitCaption,
  toTableUnitCaptionProps,
} from "@/components/common/table-unit-caption";
import { BalanceTypeChip } from "./balance-type-chip";
import { copyTableToClipboard, TransposeToggle } from "./table-utils";
import {
  selectableCellAttrs,
  type TableCellPasteEntry,
  type TableCellSelectionEntry,
  useTableCellSelection,
} from "@/hooks/use-table-cell-selection";
import { PreviewIssuesSection } from "./preview-issues-section";
import { getFilteredPreviewIssues } from "./preview-issue-utils";
import { ScrollbarArea } from "@/toggle-ui-kit/scrollbar";

interface EditableCellProps {
  item: EditableRowItemV2;
  rowIndex: number;
  colIndex: number;
  transpose: boolean;
  roleKey: string;
  targetYear: "CURRENT" | "PREVIOUS";
  uniqueId: string;
  onCellMouseDown?: (uniqueId: string, event: React.MouseEvent) => void;
  onCellMouseEnter?: (event: React.MouseEvent) => void;
  onCellMouseUp?: () => void;
  onCellUpdate: (
    roleKey: string,
    targetYear: "CURRENT" | "PREVIOUS",
    cellTagKey: string,
    value: string,
    originalValue: string,
    sourceDsdAddress?: string | null,
  ) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({
  item,
  rowIndex,
  colIndex,
  transpose,
  roleKey,
  targetYear,
  uniqueId,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
  onCellUpdate,
}) => {
  const {
    colspan,
    rowspan,
    value,
    type,
    isEdited,
    originalValue,
    isSumValueDifferentWithPreviousValue,
    isSumValueSame,
    balanceType,
    id,
  } = item;

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleMouseDown = (event: React.MouseEvent) => {
    if (type === "th" || !onCellMouseDown || isEditing) return;
    event.preventDefault();
    onCellMouseDown(uniqueId, event);
  };

  const handleMouseEnter = (event: React.MouseEvent) => {
    if (type === "th" || !onCellMouseEnter || isEditing) return;
    const isLeftButtonPressed =
      event.buttons === 1 || event.nativeEvent.buttons === 1;
    if (isLeftButtonPressed) {
      event.preventDefault();
      onCellMouseEnter(event);
    }
  };

  const handleMouseUp = (event: React.MouseEvent) => {
    if (type === "th" || !onCellMouseUp || isEditing) return;
    event.preventDefault();
    onCellMouseUp();
  };

  const handleStartEdit = () => {
    if (type === "th") return;
    setEditValue(value);
    setIsEditing(true);
  };

  const handleConfirmEdit = () => {
    setIsEditing(false);
    if (editValue !== value) {
      onCellUpdate(
        roleKey,
        targetYear,
        id,
        editValue,
        originalValue ?? value,
        item.sourceDsdAddress,
      );
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValue(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleConfirmEdit();
    else if (e.key === "Escape") handleCancelEdit();
  };

  const handleRestore = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEdited && originalValue !== undefined) {
      onCellUpdate(
        roleKey,
        targetYear,
        id,
        originalValue,
        originalValue,
        item.sourceDsdAddress,
      );
    }
  };

  const getCellBackgroundStyle = () => {
    if (type === "th") return "bg-neutral-50";
    if (isEdited) return "bg-amber-50";
    if (isSumValueDifferentWithPreviousValue) return "bg-red-100";
    return "bg-white";
  };

  const cellClassName = cn(
    "border border-neutral-200 px-3 py-2 text-center label-1-normal-regular text-primary h-[inherit]",
    getCellBackgroundStyle(),
    type !== "th" && "text-right",
    "min-w-[120px] max-w-[400px] whitespace-normal break-words px-[12px] py-[6px]",
    type !== "th" &&
      "cursor-pointer hover:bg-amber-100 transition-colors duration-200 select-none",
    isEdited && "border-amber-300",
  );

  const cellProps = {
    colSpan: colspan > 1 ? colspan : undefined,
    rowSpan: rowspan > 1 ? rowspan : undefined,
    "data-row": rowIndex,
    "data-col": colIndex,
    "data-cell-key": `${rowIndex}-${colIndex}`,
    className: cellClassName,
  };

  const tdProps = {
    ...cellProps,
    ...selectableCellAttrs({ uniqueId, cellId: id, displayValue: value }),
    "data-role-key": roleKey,
    "data-target-year": targetYear,
    "data-original-value": originalValue ?? value,
    "data-raw-value": value,
    "data-edited": isEdited ? "" : undefined,
    "data-source-dsd-address": item.sourceDsdAddress ?? undefined,
  };

  if (type === "th") {
    return (
      <th {...cellProps}>
        <div
          className={cn(
            "flex gap-1",
            transpose
              ? "flex-col items-start h-full"
              : "flex-row justify-between",
          )}
        >
          <div className="flex-1 text-wrap text-left">{value}</div>
          <div className="flex-0">
            <BalanceTypeChip balanceType={balanceType} />
          </div>
        </div>
      </th>
    );
  }

  const hasTagInfo =
    item.lineItemLabel || (item.memberLabels && item.memberLabels.length > 0);

  const tagTooltipLabel = hasTagInfo ? (
    <div className="flex flex-col gap-0.5 text-left">
      {item.lineItemLabel && (
        <div>
          <span className="opacity-60">라인아이템: </span>
          <span>{item.lineItemLabel}</span>
        </div>
      )}
      {item.memberLabels && item.memberLabels.length > 0 && (
        <div>
          <span className="opacity-60">멤버: </span>
          <span>{item.memberLabels.join(", ")}</span>
        </div>
      )}
    </div>
  ) : null;

  const tdContent = (
    <td
      {...tdProps}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseUp={handleMouseUp}
      onDoubleClick={handleStartEdit}
    >
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleConfirmEdit}
          onKeyDown={handleKeyDown}
          title="셀 값 편집"
          className="w-full bg-white border border-blue-400 rounded px-2 py-1 text-right text-sm outline-none ring-2 ring-blue-200"
        />
      ) : (
        <div className="flex items-center justify-end gap-1">
          {isSumValueDifferentWithPreviousValue && (
            <ArrowedButtonTooltip
              label="계산된 합계열은 태깅된 합계열의 값과 다릅니다."
              side="bottom"
            >
              <Icon className="text-black w-[16px] h-[16px]" iconNode={[]}>
                <CircleAlert className="text-red-500" />
              </Icon>
            </ArrowedButtonTooltip>
          )}
          {isSumValueSame && (
            <Icon className="text-black w-[16px] h-[16px]" iconNode={[]}>
              <CircleCheck className="text-green-500" />
            </Icon>
          )}
          <span>{value}</span>
          {isEdited && (
            <ArrowedButtonTooltip
              label={`원본값: ${originalValue}`}
              side="bottom"
            >
              <button
                type="button"
                title="원본값으로 복원"
                onClick={handleRestore}
                className="ml-1 p-0.5 rounded hover:bg-amber-200 transition-colors"
              >
                <Undo2 className="w-3 h-3 text-amber-600" />
              </button>
            </ArrowedButtonTooltip>
          )}
        </div>
      )}
    </td>
  );

  if (!hasTagInfo || isEditing) return tdContent;

  return (
    <TooltipWrapper
      label={tagTooltipLabel}
      side="top"
      sideOffset={4}
      styles={{
        contentStyle: "border-none p-0 max-w-[320px]",
        bgStyle: "bg-neutral-900 px-2 py-1.5",
        textStyle: "caption-2-regular text-white",
      }}
    >
      {tdContent}
    </TooltipWrapper>
  );
};

interface EditableTableWithCopyProps {
  table: EditableTableItemsV2;
  tableIndex: number;
  targetYear: "CURRENT" | "PREVIOUS";
  onCellUpdate: (
    roleKey: string,
    targetYear: "CURRENT" | "PREVIOUS",
    cellTagKey: string,
    value: string,
    originalValue: string,
    sourceDsdAddress?: string | null,
  ) => void;
  onCellUpdateBatch: (updates: UpdateCellValueRequest[]) => void;
}

const EditableTableWithCopy = ({
  table,
  tableIndex,
  targetYear,
  onCellUpdate,
  onCellUpdateBatch,
}: EditableTableWithCopyProps) => {
  const [copied, setCopied] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);

  const buildCellUpdate = useCallback(
    (
      cellId: string,
      value: string,
      element: HTMLElement,
    ): UpdateCellValueRequest | null => {
      const roleKey = element.getAttribute("data-role-key");
      const targetYearValue = element.getAttribute("data-target-year");
      if (
        !roleKey ||
        (targetYearValue !== "CURRENT" && targetYearValue !== "PREVIOUS")
      ) {
        return null;
      }

      return {
        roleKey,
        targetYear: targetYearValue,
        cellTagKey: cellId,
        value,
        originalValue:
          element.getAttribute("data-original-value") ??
          element.getAttribute("data-raw-value") ??
          "",
        sourceDsdAddress: element.getAttribute("data-source-dsd-address"),
      };
    },
    [],
  );

  const handleEnterEdit = useCallback(
    (_cellId: string, uniqueId?: string, cell?: HTMLElement) => {
      const targetCell =
        cell ??
        (uniqueId
          ? (tableRef.current?.querySelector(
              `td#${CSS.escape(uniqueId)}`,
            ) as HTMLTableCellElement | null)
          : null);
      targetCell?.dispatchEvent(new MouseEvent("dblclick", { bubbles: true }));
    },
    [],
  );

  const handlePasteValues = useCallback(
    (entries: TableCellPasteEntry[]) => {
      const updates = entries
        .map(({ cellId, value, element }) => {
          const currentValue = element.getAttribute("data-raw-value") ?? "";
          if (value === currentValue) return null;
          return buildCellUpdate(cellId, value, element);
        })
        .filter((update): update is UpdateCellValueRequest => !!update);

      if (updates.length > 0) {
        onCellUpdateBatch(updates);
      }
      return { applied: updates.length, failed: 0 };
    },
    [buildCellUpdate, onCellUpdateBatch],
  );

  const handleDeleteValues = useCallback(
    (_cellIds: string[], cells: TableCellSelectionEntry[]) => {
      const updates = cells
        .map(({ cellId, element }) => {
          const currentValue = element.getAttribute("data-raw-value") ?? "";
          if (currentValue.trim() === "") return null;
          return buildCellUpdate(cellId, "", element);
        })
        .filter((update): update is UpdateCellValueRequest => !!update);

      if (updates.length > 0) {
        onCellUpdateBatch(updates);
      }
    },
    [buildCellUpdate, onCellUpdateBatch],
  );

  const { handleCellMouseDown, handleCellMouseEnter, handleCellMouseUp } =
    useTableCellSelection(tableRef, {
      styleId: `editable-preview-cell-selection-${tableIndex}`,
      onEnterEdit: handleEnterEdit,
      onPasteValues: handlePasteValues,
      onDeleteValues: handleDeleteValues,
    });

  const { updateTransposeStateMutate } = useUpdateTransposeState();
  const { updateAutoSumStateMutate, isPending: isAutoSumPending } =
    useUpdateAutoSumState();

  const handleCopy = async () => {
    if (!tableRef.current) return;

    const tableElement = tableRef.current.querySelector(
      "table",
    ) as HTMLTableElement;
    if (!tableElement) {
      toast.error("복사 실패: 테이블을 찾을 수 없습니다.");
      return;
    }

    const success = await copyTableToClipboard(tableElement);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("테이블의 내용이 복사되었습니다.");
    } else {
      toast.error(
        "복사 실패: 테이블의 내용을 복사하는 중 오류가 발생했습니다.",
      );
    }
  };

  return (
    <div
      className="w-full max-w-full relative group min-w-0 overflow-x-hidden"
      ref={tableRef}
    >
      <div className="flex items-center justify-between pb-[8px]">
        <div className="flex items-center gap-[12px]">
          <TransposeToggle
            transpose={table.transpose}
            setTranspose={(transpose) => {
              updateTransposeStateMutate({ roleKey: table.roleKey, transpose });
            }}
          />
          <AutoSumToggle
            useAutoSum={table.useAutoSum}
            setUseAutoSum={(useAutoSum) => {
              updateAutoSumStateMutate({ roleKey: table.roleKey, useAutoSum });
            }}
          />
        </div>
        <Button
          onClick={handleCopy}
          className="bg-white/90 hover:bg-white border shadow-none p-2"
          leftIcon={
            copied ? (
              <Check className="h-4 w-4 text-green-600" />
            ) : (
              <Copy className="h-4 w-4 text-gray-600" />
            )
          }
        >
          {copied ? "복사됨" : "전체 복사"}
        </Button>
      </div>

      <div className="text-xs text-gray-500 mb-2 truncate">
        💡 <strong>사용법:</strong> 더블클릭하여 값을 수정(Enter 확인, Esc
        취소), 드래그하여 영역 선택 후 Ctrl+C로 복사합니다.
      </div>

      <div className="relative">
        <TableUnitCaption
          className="flex flex-row items-end justify-end mb-[12px]"
          {...toTableUnitCaptionProps(table)}
        />

        <div className="w-full overflow-y-auto min-w-0 max-w-full">
          <ScrollbarArea>
            <table className="border-collapse border border-gray-300">
              <tbody>
                {table.rowItems.map(
                  (row: EditableRowItemV2[], rowIndex: number) => (
                    <tr key={`${tableIndex}-row-${rowIndex}`} className="h-px">
                      {row.map((item, cellIndex) => (
                        <EditableCell
                          key={`${tableIndex}-${rowIndex}-${cellIndex}-${item.id}`}
                          item={item}
                          rowIndex={rowIndex}
                          colIndex={cellIndex}
                          transpose={table.transpose}
                          roleKey={table.roleKey}
                          targetYear={targetYear}
                          uniqueId={`editable-preview-${targetYear}-${tableIndex}-${rowIndex}-${cellIndex}`}
                          onCellMouseDown={handleCellMouseDown}
                          onCellMouseEnter={handleCellMouseEnter}
                          onCellMouseUp={handleCellMouseUp}
                          onCellUpdate={onCellUpdate}
                        />
                      ))}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </ScrollbarArea>
        </div>

        {isAutoSumPending && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              합계 반영 중...
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const EditablePreviewTable = ({
  isError,
  editablePreview,
}: {
  isError: boolean;
  editablePreview: EditableTablePreviewResponseV2;
}) => {
  const { targetYear: period } = usePreviewStore((state) => state);
  const { mutate: updateCells } = useUpdateCellValue();

  const tables =
    period === "CURRENT"
      ? editablePreview?.current.tables
      : editablePreview?.prior.tables;

  const handleCellUpdate = (
    roleKey: string,
    targetYear: "CURRENT" | "PREVIOUS",
    cellTagKey: string,
    value: string,
    originalValue: string,
    sourceDsdAddress?: string | null,
  ) => {
    updateCells([
      {
        roleKey,
        targetYear,
        cellTagKey,
        value,
        originalValue,
        sourceDsdAddress,
      },
    ]);
  };

  const handleCellUpdateBatch = (updates: UpdateCellValueRequest[]) => {
    updateCells(updates);
  };

  const { filteredOmittedValues, filteredDuplicatedValues } =
    getFilteredPreviewIssues(
      editablePreview.omittedValues,
      editablePreview.duplicatedValues,
      period,
    );

  if (isError) {
    return (
      <div className="flex flex-col p-[16px] items-center justify-center gap-[16px] min-h-[400px] bg-white rounded-2xl">
        <span className="label-1-normal-medium text-red-500">
          데이터를 불러오는 중 오류가 발생했습니다.
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[24px] w-full min-w-0">
      {tables?.map((table: EditableTableItemsV2, tableIndex: number) => (
        <div
          className="flex flex-col p-[16px] items-start gap-[10px] min-w-0 bg-white rounded-2xl border border-amber-200"
          key={`table-container-${tableIndex}`}
        >
          <div
            key={`current-table-${tableIndex}`}
            className="flex flex-col w-full gap-[12px]"
          >
            <div className="flex flex-col items-start gap-[4px]">
              <span className="headline-2-semibold">{table.tableName}</span>
            </div>
            <EditableTableWithCopy
              key={`table-${tableIndex}`}
              table={table}
              tableIndex={tableIndex}
              targetYear={period}
              onCellUpdate={handleCellUpdate}
              onCellUpdateBatch={handleCellUpdateBatch}
            />
          </div>
        </div>
      ))}
      <PreviewIssuesSection
        omittedValues={filteredOmittedValues}
        duplicatedValues={filteredDuplicatedValues}
      />
    </div>
  );
};
